import React, { useRef, useEffect, useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import styles from "./index.module.scss";
import { extractFilenameFromUrl, timeStringToSeconds } from "./utils";
import { generateThumbnailFromVideo } from "logic/src/objects/video/utils";
import { getCacheFile } from "logic/src/services/richmodal";
import { IVideoFileUploadInfo } from "logic/src/objects/video/types";

// 组件属性接口
interface VideoPlayerProps {
  videos: IVideoFileUploadInfo[]; // 视频列表，直接使用IVideoFileUploadInfo类型
  initialIndex?: number; // 初始播放的视频索引
  onVideoChange?: (index: number) => void; // 视频变更回调
  className?: string; // 自定义样式类
  autoPlay?: boolean; // 是否自动播放
  autoGenerateThumbnails?: boolean; // 是否自动生成缩略图
  currentVideoInfo?: {
    index?: number; // 要定位到的视频索引（从1开始）
    seekTime?: string; // 要定位到的时间戳，格式为 "MM:SS"
  };
  resetSeekTime?: () => void; // 重置时间戳的回调函数
}

/**
 * 生成缩略图缓存键
 * @param fileId 文件ID
 * @returns 缩略图缓存键
 */
const generateThumbnailCacheKey = (fileId: string) => {
  if (!fileId) return "";
  // 直接使用文件ID作为缓存键，添加thumb_前缀区分缩略图
  return `${fileId}`;
};

/**
 * 多视频预览播放器组件
 * 完全受控组件，依赖外部传入的videoIndex和seekTime
 */
const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videos,
  className = "",
  autoPlay = true,
  autoGenerateThumbnails = true,
  currentVideoInfo = {
    index: 1,
    seekTime: "00:00",
  },
  onVideoChange,
  resetSeekTime,
}) => {
  // 视频加载状态
  const [isLoading, setIsLoading] = useState(true);
  // 视频错误状态
  const [error, setError] = useState<string | null>(null);
  // 生成的缩略图列表
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  // 是否正在加载缩略图
  const [isLoadingThumbnails, setIsLoadingThumbnails] = useState(false);
  // 视频DOM引用
  const videoRef = useRef<HTMLVideoElement>(null);
  // 是否已经应用了时间戳跳转
  const hasAppliedSeek = useRef<boolean>(false);
  // 上一次应用的视频索引和时间戳
  const lastAppliedRef = useRef<{ index?: number; time?: string }>({});

  const { index: videoIndex, seekTime = "00:00" } = currentVideoInfo || {};
  // 实际使用的视频索引（将从1开始转为从0开始）
  const actualIndex = (videoIndex || 1) - 1;
  // 当前播放的视频
  const currentVideo =
    videos[actualIndex >= 0 && actualIndex < videos.length ? actualIndex : 0] ||
    null;

  // 首次加载初始化进度条和视频序号为1
  useEffect(() => {
    if (onVideoChange) {
      onVideoChange(0);
    }
    if (resetSeekTime) {
      resetSeekTime();
    }
    // if (videoRef.current) {
    //   videoRef.current.currentTime = 0;
    //   videoRef.current.play();
    // }
  }, []);

  // 当videoIndex或seekTime变化时，更新视频显示和时间戳
  useEffect(() => {
    console.log(
      `外部Props变更: videoIndex=${videoIndex}, seekTime=${seekTime}`,
    );

    if (!videos.length) return;

    const targetIndex = (videoIndex || 1) - 1;
    if (targetIndex < 0 || targetIndex >= videos.length) {
      console.warn(`索引越界: ${targetIndex}, 视频数组长度: ${videos.length}`);
      return;
    }

    // 检查是否与上次应用的值相同
    const sameIndex = lastAppliedRef.current.index === videoIndex;
    // const sameTime = lastAppliedRef.current.time === seekTime;

    // 如果视频索引变化了，需要重置视频元素
    if (!sameIndex) {
      console.log(
        `切换到新视频: 索引=${videoIndex}, 目标视频URL=${currentVideo?.url}`,
      );
      setIsLoading(true);
      setError(null);

      // 暂停当前视频
      if (videoRef.current) {
        // videoRef.current.pause();
      }

      // 重置时间戳应用标志
      hasAppliedSeek.current = false;

      // 强制更新视频src
      if (videoRef.current && currentVideo) {
        console.log(`强制更新视频元素src为: ${currentVideo.url}`);
        videoRef.current.src = currentVideo.url;
      }
    }
    // 如果只有时间戳变化，需要跳转到新的时间点
    else if (videoRef.current) {
      console.log(`时间戳变化: ${seekTime}, 当前视频不变`);
      hasAppliedSeek.current = false;

      // 如果视频已就绪，立即应用时间跳转
      if (videoRef.current.readyState >= 2) {
        applySeekTime();
      }
    }

    // 记录这次应用的值
    lastAppliedRef.current = { index: videoIndex, time: seekTime };
  }, [currentVideoInfo, videos, currentVideo]);

  // 确保视频元素的src随currentVideo更新
  useEffect(() => {
    if (videoRef.current && currentVideo) {
      const currentSrc = videoRef.current.src;
      const targetSrc = currentVideo.url;

      // 如果currentVideo变化但src相同，我们需要重新加载视频
      if (currentSrc !== targetSrc) {
        console.log(`更新视频元素src: 从${currentSrc} 到 ${targetSrc}`);

        // 暂停当前视频
        videoRef.current.pause();

        // 更新视频源
        videoRef.current.src = targetSrc;

        // 重置跳转标志
        hasAppliedSeek.current = false;

        // 加载新视频
        videoRef.current.load();

        // 确保新加载的视频在加载完成后保持暂停状态，除非有seekTime
        const shouldStartFromBeginning = !seekTime || seekTime === "00:00";
        if (shouldStartFromBeginning) {
          console.log("视频切换: 确保从头开始且暂停");
          const handler = () => {
            if (videoRef.current) {
              videoRef.current.currentTime = 0;
              videoRef.current.pause();
              videoRef.current.removeEventListener("loadeddata", handler);
            }
          };
          videoRef.current.addEventListener("loadeddata", handler);
        }
      }
    }
  }, [currentVideo, seekTime]);

  // 在视频加载完成/可播放时应用时间跳转
  const applySeekTime = () => {
    if (!videoRef.current || hasAppliedSeek.current) return;

    const seconds = timeStringToSeconds(seekTime);
    if (videoRef.current.readyState >= 2) {
      if (seconds >= 0 && seconds <= videoRef.current.duration) {
        console.log(
          `应用时间跳转: ${seekTime} (${seconds}秒), 视频时长: ${videoRef.current.duration}秒`,
        );
        videoRef.current.currentTime = seconds;

        // 从锚点点击时，始终开始播放
        console.log("从锚点点击，开始自动播放");
        videoRef.current.play().catch(err => {
          console.error("跳转后自动播放失败:", err);
        });

        hasAppliedSeek.current = true;
      } else {
        console.log(
          `时间点无效或超出范围: ${seekTime} (${seconds}秒), 视频时长: ${videoRef.current.duration}秒`,
        );
      }
    } else {
      console.log("视频尚未准备好，等待加载...");
    }
  };

  // 视频加载完成处理
  const handleVideoLoaded = () => {
    console.log(`视频加载完成: ${currentVideo?.url}`);
    setIsLoading(false);

    // 尝试应用时间跳转
    if (!hasAppliedSeek.current) {
      applySeekTime();
    } else if (autoPlay && videoRef.current) {
      // 如果没有跳转到特定时间点，且开启了自动播放，则开始播放
      videoRef.current.play().catch(err => {
        console.error("自动播放失败:", err);
      });
    }
  };

  // 处理缩略图点击 - 完全依赖外部model
  const handleThumbnailClick = (index: number) => {
    console.log(`缩略图点击: 索引=${index + 1}，通知外部更新model`);

    // 首先重置时间戳为00:00，防止切换冲突
    if (resetSeekTime) {
      console.log(`重置时间戳到00:00`);
      resetSeekTime();
    }

    // 稍微延迟更新视频索引，避免与时间戳重置冲突
    setTimeout(() => {
      // 通知父组件更新视频索引
      if (onVideoChange) {
        console.log(`发送视频索引更新事件: ${index + 1}`);
        onVideoChange(index);
      }

      // 确保视频从0位置开始且处于暂停状态
      setTimeout(() => {
        if (videoRef.current) {
          console.log("确保新视频从头开始且暂停");
          videoRef.current.currentTime = 0;
          videoRef.current.pause();
        }
      }, 50);
    }, 10);
  };

  // 获取缩略图的逻辑
  useEffect(() => {
    if (videos.length > 0) {
      // 先尝试从缓存获取缩略图
      fetchCachedThumbnails();

      // 如果需要自动生成缩略图（没有缓存的情况下）
      if (autoGenerateThumbnails) {
        generateMissingThumbnails();
      }
    }
  }, [videos, autoGenerateThumbnails]);

  // 从缓存获取所有视频的缩略图
  const fetchCachedThumbnails = async () => {
    // 获取所有有文件ID且没有缩略图URL的视频
    const videosNeedThumbnail = videos.filter(
      video => video.fileId && !video.thumbnailUrl && !thumbnails[video.url],
    );

    if (videosNeedThumbnail.length === 0) return;

    setIsLoadingThumbnails(true);

    try {
      // 生成所有需要的缩略图缓存键 - 直接使用文件ID加前缀
      const cacheKeys = videosNeedThumbnail
        .map(video =>
          video.fileId ? generateThumbnailCacheKey(video.fileId) : "",
        )
        .filter(key => key !== "");

      if (cacheKeys.length === 0) {
        setIsLoadingThumbnails(false);
        return;
      }

      console.log("尝试从缓存获取缩略图，使用文件ID:", cacheKeys);

      // 从缓存接口获取缩略图
      const cachedFiles = await getCacheFile(cacheKeys);

      if (!cachedFiles || cachedFiles.length === 0) {
        console.log("未找到缓存的缩略图");
        setIsLoadingThumbnails(false);
        return;
      }

      // 将缓存的缩略图URL添加到缩略图状态中
      const newThumbnails: Record<string, string> = {};

      // 匹配缓存的缩略图与视频 - 使用thumb_前缀的文件ID作为匹配键
      videosNeedThumbnail.forEach(video => {
        if (!video.fileId) return;

        const thumbKey = generateThumbnailCacheKey(video.fileId);
        const cached = cachedFiles.find(file => file.key === thumbKey);

        if (cached && cached.url) {
          console.log(`找到视频 ${video.fileId} 的缓存缩略图:`, cached.url);
          newThumbnails[video.url] = cached.url;
        }
      });

      if (Object.keys(newThumbnails).length > 0) {
        setThumbnails(prev => ({ ...prev, ...newThumbnails }));
      }
    } catch (error) {
      console.error("获取缓存缩略图失败:", error);
    } finally {
      setIsLoadingThumbnails(false);
    }
  };

  // 生成缺失的缩略图（只处理没有从缓存获取到的）
  const generateMissingThumbnails = async () => {
    // 等待缓存获取完成
    if (isLoadingThumbnails) return;

    const newThumbnails: Record<string, string> = {};

    for (let i = 0; i < videos.length; i++) {
      const video = videos[i];

      // 如果已经有缩略图URL，或者已为这个URL生成过缩略图，则跳过
      if (video.thumbnailUrl || thumbnails[video.url]) continue;

      try {
        // 生成缩略图，从视频的第一帧截取
        console.log(`为视频 ${i} 生成缩略图`);
        const dataUrl = await generateThumbnailFromVideo(video.url);
        newThumbnails[video.url] = dataUrl;
      } catch (err) {
        console.error(`生成视频 ${i} 的缩略图失败:`, err);
      }
    }

    // 更新缩略图状态
    if (Object.keys(newThumbnails).length > 0) {
      setThumbnails(prev => ({ ...prev, ...newThumbnails }));
    }
  };

  // 生成视频名称
  const getVideoName = (video: IVideoFileUploadInfo) => {
    if (video.file?.name) return video.file.name;
    return extractFilenameFromUrl(video.url);
  };

  // 生成缩略图URL
  const getThumbnailUrl = (video: IVideoFileUploadInfo) => {
    // 优先使用视频提供的缩略图
    if (video.thumbnailUrl) return video.thumbnailUrl;

    // 其次使用生成的缩略图
    if (thumbnails[video.url]) return thumbnails[video.url];

    return "";
  };

  // 如果没有视频
  if (!videos || videos.length === 0) {
    return (
      <div className={`${styles.videoPlayerEmpty} ${className}`}>
        没有可播放的视频
      </div>
    );
  }

  return (
    <div className={`${styles.videoPlayerContainer} ${className}`}>
      {/* 中间视频播放区域 */}
      <div className={styles.videoWrapper}>
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <LoadingOutlined className={styles.loadingIcon} />
            <span>正在加载视频...</span>
          </div>
        )}

        {error && (
          <div className={styles.errorOverlay}>
            <span className={styles.errorText}>{error}</span>
          </div>
        )}

        {currentVideo && (
          <video
            ref={videoRef}
            className={styles.videoPlayer}
            src={currentVideo.url}
            controls
            onLoadedMetadata={() => {
              console.log(`视频元数据加载完成，可以获取时长信息`);
              // 元数据加载后可尝试应用时间跳转
              if (!hasAppliedSeek.current) {
                applySeekTime();
              }
            }}
            onLoadedData={handleVideoLoaded}
            onError={() => {
              console.error("视频加载错误");
              setIsLoading(false);
              setError("视频加载失败，请检查视频链接是否有效");
            }}
            onPlaying={() => setIsLoading(false)}
            onTimeUpdate={() => {
              // 检查时间戳是否正确应用
              if (
                videoRef.current &&
                !hasAppliedSeek.current &&
                videoRef.current.currentTime > 0
              ) {
                const seconds = timeStringToSeconds(seekTime);
                if (Math.abs(videoRef.current.currentTime - seconds) > 0.5) {
                  console.log(
                    `时间戳修正: 当前=${videoRef.current.currentTime.toFixed(
                      2,
                    )}秒, 目标=${seconds}秒`,
                  );
                  videoRef.current.currentTime = seconds;

                  // 只有当时间戳不是"00:00"时才自动播放
                  if (seekTime !== "00:00") {
                    videoRef.current.play().catch(err => {
                      console.error("时间戳修正后自动播放失败:", err);
                    });
                  } else {
                    // 如果时间戳是00:00，则确保视频暂停
                    videoRef.current.pause();
                  }
                }
                hasAppliedSeek.current = true;
              }
            }}
          />
        )}
      </div>

      {/* 底部缩略图列表 */}
      {videos.length > 1 && (
        <div className={styles.thumbnailContainer}>
          <div className={styles.thumbnailScroller}>
            {videos.map((video, index) => (
              <div
                key={`thumb-${index}`}
                className={`${styles.thumbnailItem} ${
                  index === actualIndex ? styles.activeThumbnail : ""
                }`}
                onClick={() => handleThumbnailClick(index)}
              >
                <div className={styles.thumbnailImageWrapper}>
                  {getThumbnailUrl(video) && (
                    <img
                      src={getThumbnailUrl(video)}
                      alt={getVideoName(video)}
                      className={styles.thumbnailImage}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* <div className={styles.tips}>
        <div className={styles.tipsHeader}>视频理解耗时较长，请您耐心等候</div>
        <div className={styles.tipsBody}>
          <div className={styles.tipsItem}>时长0~1分钟，理解耗时约为30s</div>
          <div className={styles.tipsItem}>时长1~3分钟，理解耗时约为1分钟</div>
          <div className={styles.tipsItem}>
            时长超过3分钟，理解耗时约为1~3分钟
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default VideoPlayer;
